import { computed, onScopeDispose, ref, watch, type Ref } from 'vue'
import { EventNameEnum } from '@/types/generated'
import { useMetrics } from '@/composables/useMetrics'
import type { VerdictChunk } from '@/composables/useVerdictChunks'
import { VERDICT_SOURCE_PROVIDER } from '@/composables/useVerdictLabels'

interface UseVerdictCitationPopoverOptions {
  registry: Map<string, VerdictChunk>
  chatType: Ref<string>
  messageUuid: Ref<string>
}

const POPOVER_HIDE_DELAY_MS = 120
const POPOVER_WIDTH_PX = 320
const VIEWPORT_EDGE_PADDING_PX = 8

export function useVerdictCitationPopover({
  registry,
  chatType,
  messageUuid
}: UseVerdictCitationPopoverOptions) {
  const { trackEvent } = useMetrics()

  const hoveredCitation = ref<{ verdict: VerdictChunk; rect: DOMRect } | null>(null)
  let hidePopoverTimer: ReturnType<typeof setTimeout> | null = null

  watch(
    () => hoveredCitation.value?.verdict.court_judgement_uuid ?? null,
    (uuid) => {
      if (!uuid) return
      void trackEvent(EventNameEnum.VERDICT_PREVIEW_OPENED, {
        court_judgement_uuid: uuid,
        message_uuid: messageUuid.value,
        chat_type: chatType.value,
        source_provider: VERDICT_SOURCE_PROVIDER
      })
    }
  )

  const showCitationPopover = (num: string, el: Element) => {
    if (hidePopoverTimer) {
      clearTimeout(hidePopoverTimer)
      hidePopoverTimer = null
    }
    const verdict = registry.get(num)
    if (!verdict) return
    hoveredCitation.value = { verdict, rect: el.getBoundingClientRect() }
  }

  const scheduleCitationHide = () => {
    hidePopoverTimer = setTimeout(() => {
      hoveredCitation.value = null
    }, POPOVER_HIDE_DELAY_MS)
  }

  const keepCitationOpen = () => {
    if (hidePopoverTimer) {
      clearTimeout(hidePopoverTimer)
      hidePopoverTimer = null
    }
  }

  const citationPopoverStyle = computed(() => {
    if (!hoveredCitation.value) return {}
    const { rect } = hoveredCitation.value
    const maxLeft = window.innerWidth - POPOVER_WIDTH_PX - VIEWPORT_EDGE_PADDING_PX
    let left = rect.left
    if (left > maxLeft) left = maxLeft
    if (left < VIEWPORT_EDGE_PADDING_PX) left = VIEWPORT_EDGE_PADDING_PX
    return {
      position: 'fixed' as const,
      top: `${rect.bottom + VIEWPORT_EDGE_PADDING_PX}px`,
      left: `${left}px`
    }
  })

  onScopeDispose(() => {
    if (hidePopoverTimer) clearTimeout(hidePopoverTimer)
  })

  return {
    hoveredCitation,
    showCitationPopover,
    scheduleCitationHide,
    keepCitationOpen,
    citationPopoverStyle
  }
}
